import { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useApi } from "src/hooks/useApi";
import { useAuth } from "src/context/AuthProvider";
import { TbCheck, TbAlertCircle, TbChevronDown, TbDownload, TbRefresh, TbX, TbLeaf, TbCloudOff, TbCoinEuro } from "react-icons/tb";
import PageHeader from "src/components/PageHeader/PageHeader";
import { IoArrowBack, IoChevronForward } from "react-icons/io5";
import { downloadChargesReceipt } from "src/services/downloadChargesReceipt";
import Dunning from "src/components/Dunning/Dunning";
import { isAdmin as checkAdmin } from "src/utils/roles";
import Popup from "src/components/Popup/Popup";
import { apiRequest } from "src/services/apiClient";
import { normalizeIban, validateIban } from "src/helpers/sanitizeInput";
import { PLAN_PRICES } from "src/pages/Register/registerTypes";
import { computePvsComparison, type PvsComparisonSettings } from "src/helpers/pvsComparison";
import "./charges.scss";
import "src/pages/Register/register.scss";

const PLANS = [
    { id: 1, nameKey: "register.planBasic",      price: PLAN_PRICES[1] },
    { id: 2, nameKey: "register.planPlus",       price: PLAN_PRICES[2] },
    { id: 3, nameKey: "register.planPro",        price: PLAN_PRICES[3] },
];


interface InvoiceRow {
    invoiceId:    string;
    status:       number;
    dunning?:     number;
    amount:       number;
    recipientId:  string;
    patientId:    string;
    recipient:    string;
    patient:      string;
    doctor?:      string;
    creationDate: string;
}

interface SmsRow {
    sentDate: string;
    cost: number;
    kind: "sms" | "otp";
    recipient: { name: string; tel?: string };
}

interface LetterRow {
    sentDate: string;
    letterXpressId: string;
    cost: number;
    letterColor?: boolean;
    letterDuplex?: boolean;
    letterEnvelope?: string;
    letterDispatch?: string;
    letterPages?: number;
    recipient: { name: string; street: string; number: string; plz: string; city: string };
}

interface DoctorBreakdown {
    doctorId:     string;
    doctorName:   string;
    count:        number;
    amount:       number;
    allowance:    number | null;
    overageCount: number;
    overageFee:   number;
}

interface PlanSummary {
    plan:                      number;
    baseFee:                   number;
    prorationDays:             number | null;
    totalDays:                 number | null;
    includedDoctors:           number;
    doctorCount:               number;
    extraDoctors:              number;
    extraDoctorFee:            number;
    includedStaff:             number;
    staffCount:                number;
    extraStaff:                number;
    extraStaffFee:             number;
    invoiceAllowancePerDoctor: number | null;
    totalAllowance:            number | null;
    smsBilled:                 boolean;
    addOns: {
        key: "tagging" | "goaChecker";
        label: string;
        fee: number;
        billable: boolean;
        cancelEffectiveFrom: string | null;
    }[];
    addOnFee:                  number;
    removedThisPeriod:         { name: string; deletedAt: string; role: "doctor" | "staff" }[];
    roster: {
        id: string;
        name: string;
        email: string | null;
        specialty: string[] | string | null;
        lastLogin: string | null;
        userRights: number;
        role: "doctor" | "staff";
        status: "included" | "extra";
        removed: boolean;
        deletedAt: string | null;
    }[];
}

interface ChargesData {
    period: { year: number; month: number };
    paid:   boolean;
    paidAt: string | null;
    planSummary: PlanSummary;
    invoices: {
        count:      number;
        paidCount:  number;
        amount:     number;
        overageFee: number;
        byDoctor:   DoctorBreakdown[];
    };
    sms:     { count: number; rate: number; total: number };
    letters: { count: number; surcharge: number; lxTotal: number; total: number };
    subtotal:    number;
    discount:    number;
    discountPercentage?: number;
    grandTotal:  number;
    invoiceRows: InvoiceRow[];
    smsRows:     SmsRow[];
    letterRows:  LetterRow[];
}

interface BillingInfo {
    plan:               number;
    subscriptionStatus: string; // trialing | active | past_due | canceled | incomplete
    trialEndsAt:        string | null;
    mandate:            { reference: string; iban: string; status: string; signedAt: string } | null;
}

const PLAN_LABELS: Record<number, string> = { 1: "BASIC", 2: "PLUS", 3: "PRO", 4: "ENTERPRISE" };
const PLAN_COLORS: Record<number, string> = { 1: "#94a3b8", 2: "#3b82f6", 3: "#8b5cf6", 4: "#f59e0b" };

const ADDON_INFO: { key: string; labelKey: string; price: number; minPlan: number }[] = [
    { key: "tagging",    labelKey: "charges.addonTagging",    price: 9.9,  minPlan: 2 },
    { key: "goaChecker", labelKey: "charges.addonGoaChecker", price: 29.9, minPlan: 3 },
];

const MINS_SAVED_PER_INVOICE = 5;
const SHEETS_PER_INVOICE     = 2;
const CO2_PER_SHEET_G        = 10;
function maskIban(iban: string): string {
    const clean = iban.replace(/\s/g, "");
    if (clean.length <= 8) return iban;
    return clean.slice(0, 4) + " ···· " + clean.slice(-4);
}

function useCountUp(target: number, duration = 1400): number {
    const [value, setValue] = useState(0);
    const startRef = useRef<number | null>(null);
    const frameRef = useRef<number>(0);
    useEffect(() => {
        if (target === 0) { setValue(0); return; }
        startRef.current = null;
        const animate = (ts: number) => {
            if (startRef.current === null) startRef.current = ts;
            const progress = Math.min((ts - startRef.current) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setValue(Math.round(eased * target));
            if (progress < 1) frameRef.current = requestAnimationFrame(animate);
            else setValue(target);
        };
        frameRef.current = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(frameRef.current);
    }, [target, duration]);
    return value;
}

function fmtTimeSaved(t: TFunction, count: number) {
    const total = count * MINS_SAVED_PER_INVOICE;
    const h = Math.floor(total / 60);
    const min = total % 60;
    const est = h > 0
        ? t("charges.timeSavedHours", { h, min })
        : t("charges.timeSavedMinutes", { min: total });
    return { est, count };
}

function fmtCo2(count: number) {
    const sheets = count * SHEETS_PER_INVOICE;
    const co2kg  = +((sheets * CO2_PER_SHEET_G) / 1000).toFixed(2);
    return { sheets, co2kg };
}

type DrawerKey = "plan" | "doctors" | "invoices" | "sms" | "letters" | null;

type WhyItem = { title: string; detail?: string; delta: number };

function buildWhyItems(cur: ChargesData, prev: ChargesData, t: TFunction): WhyItem[] {
    const p  = cur.planSummary;
    const pp = prev.planSummary;
    const items: WhyItem[] = [];

    const overDelta = cur.invoices.overageFee - prev.invoices.overageFee;
    if (Math.abs(overDelta) > 0.01 && cur.invoices.overageFee > 0) {
        const overCount = cur.invoices.byDoctor.reduce((s, d) => s + (d.overageCount ?? 0), 0);
        const top = cur.invoices.byDoctor
            .filter((d) => d.overageFee > 0)
            .sort((a, b) => b.overageFee - a.overageFee)[0];
        items.push({
            title: top
                ? t("charges.whyInvoiceOverageDoctor", {
                    count: overCount || Math.max(0, cur.invoices.count - (p.totalAllowance ?? 0)),
                    name: top.doctorName,
                    defaultValue: "+{{count}} invoice overages on {{name}}",
                })
                : t("charges.invoiceOverage", { defaultValue: "Invoice overage" }),
            detail: p.totalAllowance != null
                ? t("charges.whyInvoiceOverageDetail", {
                    used: cur.invoices.count,
                    allowance: p.totalAllowance,
                    defaultValue: "{{used}} vs {{allowance}} included",
                })
                : undefined,
            delta: overDelta,
        });
    }

    const docDelta = p.extraDoctorFee - pp.extraDoctorFee;
    if (Math.abs(docDelta) > 0.01) {
        items.push({
            title: t("charges.whyExtraDoctors", { count: p.extraDoctors, defaultValue: "+{{count}} extra doctors" }),
            delta: docDelta,
        });
    }

    const letterDelta = cur.letters.total - prev.letters.total;
    if (Math.abs(letterDelta) > 0.01) {
        items.push({
            title: t("charges.whyLetters", { count: cur.letters.count - prev.letters.count, defaultValue: "{{count}} letters" }),
            detail: letterDelta < 0 ? t("charges.whyLettersLess", { defaultValue: "digital delivery up" }) : undefined,
            delta: letterDelta,
        });
    }

    const smsDelta = cur.sms.total - prev.sms.total;
    if (Math.abs(smsDelta) > 0.01) {
        items.push({
            title: t("charges.whySms", { count: cur.sms.count - prev.sms.count, defaultValue: "{{count}} SMS vs last month" }),
            delta: smsDelta,
        });
    }

    return items.filter((i) => Math.abs(i.delta) > 0.01).sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
}

function WhyCard({
    momDelta,
    prevLabel,
    items,
    t,
    onComparePlans,
}: {
    momDelta: number;
    prevLabel: string;
    items: WhyItem[];
    t: TFunction;
    onComparePlans?: () => void;
}) {
    const sameDir = items.filter((i) => momDelta >= 0 ? i.delta > 0 : i.delta < 0);
    if (Math.abs(momDelta) <= 1 || sameDir.length === 0) return null;
    const dir = momDelta >= 0 ? "up" : "down";
    const maxBar = Math.max(...sameDir.map((i) => Math.abs(i.delta)), 0.01);

    return (
        <section className="charges-why-card">
            <header className="charges-why-card__head">
                <h3 className="charges-why-card__title">
                    {momDelta >= 0
                        ? t("charges.whyBillHigher", {
                            amount: fmt(Math.abs(momDelta)),
                            month: prevLabel,
                            defaultValue: "Why your bill is ↑ €{{amount}} higher than {{month}}",
                        })
                        : t("charges.whyBillLower", {
                            amount: fmt(Math.abs(momDelta)),
                            month: prevLabel,
                            defaultValue: "Why your bill is ↓ €{{amount}} lower than {{month}}",
                        })}
                </h3>
                <span className="charges-why-card__aside">{t("charges.topContributors", "Top contributors")}</span>
            </header>
            <ul className="charges-why-card__list">
                {sameDir.map((item) => (
                    <li key={item.title} className="charges-why-card__item">
                        <div className="charges-why-card__item-text">
                            <span className="charges-why-card__item-title">{item.title}</span>
                            {item.detail && <span className="charges-why-card__item-detail">{item.detail}</span>}
                        </div>
                        <div className="charges-why-card__item-right">
                            <span
                                className={`charges-why-card__bar charges-why-card__bar--${item.delta >= 0 ? "up" : "down"}`}
                                style={{ width: `${Math.max(12, (Math.abs(item.delta) / maxBar) * 100)}%` }}
                            />
                            <span className={`charges-why-card__delta charges-why-card__delta--${item.delta >= 0 ? "up" : "down"}`}>
                                {item.delta >= 0 ? "+" : "−"}€{fmt(Math.abs(item.delta))}
                            </span>
                        </div>
                    </li>
                ))}
            </ul>
            {onComparePlans && (
                <p className="charges-why-card__hint">
                    <button type="button" className="charges-why-card__hint-link" onClick={onComparePlans}>
                        {t("charges.comparePlansHint", "Press U to compare plans")}
                    </button>
                </p>
            )}
        </section>
    );
}

type LineRowDef = {
    key: DrawerKey | "invoiceOverage" | null;
    label: string;
    value: string;
    sub: string;
    subWarn?: boolean;
    badge?: string;
    expandable?: boolean;
};

function eurAmount(n: number) {
    return `€${fmt(n)}`;
}

function buildMonthLineRows(displayData: ChargesData, t: TFunction): LineRowDef[] {
    const p = displayData.planSummary;
    const overCount = displayData.invoices.byDoctor.reduce((s, d) => s + (d.overageCount ?? 0), 0);
    const hasAllowance = p.totalAllowance != null;
    const showDoctorStrip =
        hasAllowance &&
        displayData.invoices.byDoctor.length > 1 &&
        displayData.invoices.byDoctor.some((d) => d.overageFee > 0);

    const removedDoctors = (p.removedThisPeriod ?? []).filter((r) => r.role === "doctor");
    const removedStaff   = (p.removedThisPeriod ?? []).filter((r) => r.role === "staff");
    const removedNote = (removed: { name: string }[]) => removed.length > 0
        ? t("charges.removedButBilled", { names: removed.map((r) => r.name).join(", "), defaultValue: "includes {{names}} — removed this month, still billed" })
        : null;

    return [
        {
            key: "plan",
            label: t("charges.rowPlan"),
            value: eurAmount(p.baseFee),
            sub: p.prorationDays != null && p.totalDays != null
                ? t("charges.planLineSubProrated", {
                    plan: PLAN_LABELS[p.plan] ?? "BASIC",
                    days: p.prorationDays,
                    total: p.totalDays,
                    defaultValue: "{{plan}} · prorated {{days}}/{{total}} days (trial period)",
                })
                : t("charges.planLineSub", {
                    plan: PLAN_LABELS[p.plan] ?? "BASIC",
                    doctors: p.includedDoctors,
                    staff: p.includedStaff,
                    defaultValue: "{{plan}} · {{doctors}} doctor + {{staff}} staff included",
                }),
        },
        {
            key: "doctors",
            label: t("charges.rowExtraDoctors", "Extra doctors"),
            value: eurAmount(p.extraDoctorFee),
            sub: p.extraDoctors > 0
                ? [
                    t("charges.doctorsOverageRate", { count: p.extraDoctors, defaultValue: "{{count}} doctors over plan · €39 each" }),
                    removedNote(removedDoctors),
                ].filter(Boolean).join(" · ")
                : t("charges.doctorsIncludedShort", { count: p.doctorCount, included: p.includedDoctors }),
            subWarn: p.extraDoctors > 0,
            badge: p.extraDoctors > 0 ? `+${p.extraDoctors}` : undefined,
        },
        {
            key: null,
            label: t("charges.rowStaff", "Staff"),
            value: eurAmount(p.extraStaffFee),
            sub: p.extraStaff > 0
                ? [
                    t("charges.staffOverage", { total: p.staffCount, included: p.includedStaff, extra: p.extraStaff }),
                    removedNote(removedStaff),
                ].filter(Boolean).join(" · ")
                : t("charges.staffIncluded", { count: p.staffCount, included: p.includedStaff }),
        },
        {
            key: "invoices",
            label: t("charges.invoices"),
            value: hasAllowance ? eurAmount(0) : t("charges.free"),
            sub: hasAllowance
                ? t("charges.invoicesAllowance", {
                    count: displayData.invoices.count,
                    total: p.totalAllowance,
                    perDoctor: p.invoiceAllowancePerDoctor ?? 0,
                })
                : t("charges.invoicesUnlimited", { count: displayData.invoices.count }),
        },
        {
            key: "invoiceOverage",
            label: t("charges.invoiceOverage", "Invoice overage"),
            value: hasAllowance ? eurAmount(displayData.invoices.overageFee) : t("charges.free"),
            sub: hasAllowance
                ? t("charges.invoiceOverageDetail", {
                    used: displayData.invoices.count,
                    total: p.totalAllowance,
                    over: overCount || Math.max(0, displayData.invoices.count - (p.totalAllowance ?? 0)),
                    defaultValue: "{{used}} of {{total}} included · 0.5% per overage",
                })
                : t("charges.invoicesUnlimited", { count: displayData.invoices.count }),
            subWarn: hasAllowance && displayData.invoices.overageFee > 0.005,
            badge: overCount > 0 ? `+${overCount}` : undefined,
            expandable: showDoctorStrip,
        },
        {
            key: "letters",
            label: t("charges.letters"),
            value: eurAmount(displayData.letters.total),
            sub: t("charges.lettersSub", { count: displayData.letters.count, defaultValue: "{{count}} sent · DHL standard" }),
        },
        {
            key: "sms",
            label: t("charges.smsOtp", "SMS & OTP"),
            value: p.smsBilled ? eurAmount(displayData.sms.total) : t("charges.free"),
            sub: t("charges.smsSub", { count: displayData.sms.count, defaultValue: "{{count}} sent · €0.09 each" }),
        },
    ];
}

function ChargesLineRow({
    row,
    doctorExpanded,
    onToggleDoctor,
    onOpen,
    byDoctor,
}: {
    row: LineRowDef;
    doctorExpanded: boolean;
    onToggleDoctor: () => void;
    onOpen: () => void;
    byDoctor: DoctorBreakdown[];
}) {
    const clickable = row.key === "invoiceOverage" ? row.expandable : !!row.key;

    return (
        <div
            className={`charges-line-item${row.subWarn ? " charges-line-item--warn" : ""}${clickable ? " charges-line-item--clickable" : ""}`}
            onClick={() => {
                if (row.key === "invoiceOverage" && row.expandable) onToggleDoctor();
                else if (row.key && row.key !== "invoiceOverage") onOpen();
            }}
        >
            <div className="charges-line-item__top">
                <div className="charges-line-item__left">
                    <span className="charges-line-item__label">{row.label}</span>
                    {row.badge && <span className="charges-overage-pill">{row.badge}</span>}
                </div>
                <span className={`charges-line-item__value${/^€0[,.]00$/.test(row.value) ? " charges-line-item__value--zero" : ""}`}>
                    {row.value}
                    {row.key === "invoiceOverage" && row.expandable && (
                        <TbChevronDown
                            size={14}
                            className={`charges-line-item__chevron${doctorExpanded ? " charges-line-item__chevron--open" : ""}`}
                        />
                    )}
                </span>
            </div>
            {row.sub && (
                <p className={`charges-line-item__sub${row.subWarn ? " charges-line-item__sub--warn" : ""}`}>{row.sub}</p>
            )}
            {row.key === "invoiceOverage" && doctorExpanded && row.expandable && (
                <div className="charges-doctor-strip" onClick={(e) => e.stopPropagation()}>
                    {byDoctor
                        .filter((d) => d.overageFee > 0)
                        .map((dr) => (
                            <div key={dr.doctorId} className="charges-doctor-strip__row">
                                <span className="charges-doctor-strip__name">{dr.doctorName}</span>
                                <span className="charges-doctor-strip__amount">€{fmt(dr.overageFee)}</span>
                            </div>
                        ))}
                </div>
            )}
        </div>
    );
}

function fmt(n: number) {
    return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDate(d?: string) {
    if (!d) return null;
    return new Date(d).toLocaleDateString(undefined, { day: "2-digit", month: "2-digit", year: "numeric" });
}

export default function Charges() {
    const { t, i18n } = useTranslation();
    const { user, updateUser } = useAuth();
    const navigate  = useNavigate();
    const now = new Date();
    const [year,  setYear]  = useState(now.getFullYear());
    const [month, setMonth] = useState(now.getMonth() + 1);
    const [open, setOpen] = useState<DrawerKey>(null);
    // Billing / SEPA
    const { data: billing, refetch: refetchBilling } = useApi<BillingInfo>("/sepa/billing");

    // Change plan / cancel
    const [changePlanOpen,   setChangePlanOpen]   = useState(false);
    const [selectedPlan,     setSelectedPlan]     = useState<number>(0);
    const [planSending,      setPlanSending]      = useState(false);
    const [planDone,         setPlanDone]         = useState(false);
    const [cancelOpen,             setCancelOpen]             = useState(false);
    const [cancelSending,          setCancelSending]          = useState(false);
    const [cancelDone,             setCancelDone]             = useState(false);
    const [cancelPlanChangeOpen,   setCancelPlanChangeOpen]   = useState(false);
    const [cancelPlanChangeSending, setCancelPlanChangeSending] = useState(false);
    const [doctorExpanded,   setDoctorExpanded]   = useState(false);
    const [pdfDownloading,   setPdfDownloading]   = useState(false);
    const [pdfError,         setPdfError]         = useState<string | null>(null);
    const [methodologyOpen,  setMethodologyOpen]  = useState(false);

    const handleChangePlan = async () => {
        if (!selectedPlan) return;
        setPlanSending(true);
        try {
            await apiRequest("/charges/change-plan", { method: "POST", body: JSON.stringify({ newPlan: selectedPlan }) }, { "Content-Type": "application/json" });
            setPlanDone(true);
        } finally { setPlanSending(false); }
    };

    const handleCancelPlanChange = async () => {
        setCancelPlanChangeSending(true);
        try {
            await apiRequest("/praxis/plan", { method: "DELETE" });
            updateUser({ praxis: { ...user!.praxis, pendingBusinessPlan: undefined, pendingBusinessPlanFrom: undefined } });
            setCancelPlanChangeOpen(false);
            refetch();
        } finally {
            setCancelPlanChangeSending(false);
        }
    };

    const handleCancelSubscription = async () => {
        setCancelSending(true);
        try {
            await apiRequest("/charges/cancel", { method: "POST" });
            setCancelDone(true);
        } finally { setCancelSending(false); }
    };

    // Update IBAN
    const [ibanOpen,   setIbanOpen]   = useState(false);
    const [ibanValue,  setIbanValue]  = useState("");
    const [bicValue,   setBicValue]   = useState("");
    const [ibanSaving, setIbanSaving] = useState(false);
    const [ibanSaved,  setIbanSaved]  = useState(false);
    const [ibanError,  setIbanError]  = useState<string | null>(null);

    // PVS savings calculator — Settings → Einstellungen Berechnung
    const { data: pvsSettings, refetch: refetchPvsSettings } = useApi<PvsComparisonSettings>("/settings");
    const [pvsOpen,       setPvsOpen]       = useState(false);
    const [pvsCommission, setPvsCommission] = useState("");
    const [pvsPerInvoice, setPvsPerInvoice] = useState("");
    const [pvsPerMonth,   setPvsPerMonth]   = useState("");
    const [pvsSaving,     setPvsSaving]     = useState(false);
    const [pvsSaved,      setPvsSaved]      = useState(false);
    const [pvsError,      setPvsError]      = useState<string | null>(null);

    const handleOpenPvs = () => {
        setPvsCommission(String(pvsSettings?.pvsCommissionPercent ?? 0));
        setPvsPerInvoice(String(pvsSettings?.pvsCostPerInvoice ?? 0));
        setPvsPerMonth(String(pvsSettings?.pvsCostPerMonth ?? 0));
        setPvsError(null);
        setPvsSaved(false);
        setPvsOpen(true);
    };

    const savePvsComparison = async (commission: number, perInvoice: number, perMonth: number) => {
        setPvsSaving(true);
        setPvsError(null);
        try {
            await apiRequest(
                "/settings/pvsComparison",
                { method: "PUT", body: JSON.stringify({ pvsCommissionPercent: commission, pvsCostPerInvoice: perInvoice, pvsCostPerMonth: perMonth }) },
                { "Content-Type": "application/json" },
            );
            setPvsSaved(true);
            refetchPvsSettings();
            setTimeout(() => { setPvsOpen(false); setPvsSaved(false); }, 1500);
        } catch (err: any) {
            setPvsError(err?.message ?? t("charges.pvsSaveError"));
        } finally {
            setPvsSaving(false);
        }
    };

    const handleSavePvs = () => {
        const commission = Number(pvsCommission);
        const perInvoice = Number(pvsPerInvoice);
        const perMonth   = Number(pvsPerMonth);
        if (!Number.isFinite(commission) || commission < 0 || commission > 100) {
            setPvsError(t("charges.pvsCommissionRangeError", "Commission must be between 0 and 100%"));
            return;
        }
        if (!Number.isFinite(perInvoice) || perInvoice < 0 || perInvoice > 50) {
            setPvsError(t("charges.pvsPerInvoiceRangeError", "Cost per invoice must be between 0 and 50"));
            return;
        }
        if (!Number.isFinite(perMonth) || perMonth < 0 || perMonth > 5000) {
            setPvsError(t("charges.pvsPerMonthRangeError", "Cost per month must be between 0 and 5000"));
            return;
        }
        void savePvsComparison(commission, perInvoice, perMonth);
    };

    const handleResetPvs = () => {
        setPvsCommission("0");
        setPvsPerInvoice("0");
        setPvsPerMonth("0");
        void savePvsComparison(0, 0, 0);
    };

    const handleOpenIban = () => {
        setIbanValue(billing?.mandate?.iban?.replace(/·/g, "").trim() ?? "");
        setBicValue("");
        setIbanError(null);
        setIbanSaved(false);
        setIbanOpen(true);
    };

    const handleDownloadReceipt = async () => {
        setPdfDownloading(true);
        setPdfError(null);
        try {
            await downloadChargesReceipt(year, month);
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : t("charges.downloadFailed", "Could not download receipt");
            setPdfError(msg);
        } finally {
            setPdfDownloading(false);
        }
    };

    const handleSaveIban = async () => {
        const clean = ibanValue.replace(/\s/g, "");
        if (!clean) { setIbanError(t("charges.ibanRequired")); return; }
        if (!validateIban(ibanValue)) { setIbanError(t("register.errorIbanFormat")); return; }
        setIbanSaving(true);
        setIbanError(null);
        try {
            await apiRequest(
                "/sepa/mandate",
                { method: "PATCH", body: JSON.stringify({ iban: ibanValue, bic: bicValue }) },
                { "Content-Type": "application/json" },
            );
            setIbanSaved(true);
            refetchBilling();
            setTimeout(() => { setIbanOpen(false); setIbanSaved(false); }, 1500);
        } catch (err: any) {
            setIbanError(err?.message ?? t("charges.ibanSaveError"));
        } finally {
            setIbanSaving(false);
        }
    };

    const userRole = user?.doctor?.userRights ?? 0;
    const { data, loading, refetch } = useApi<ChargesData>(`/charges?year=${year}&month=${month}`);

    const [addOnBusyKey, setAddOnBusyKey] = useState<string | null>(null);
    const activateAddOn = async (key: string) => {
        setAddOnBusyKey(key);
        try {
            await apiRequest(`/charges/addons/${key}/activate`, { method: "POST" });
            refetch();
        } catch {
            // toast omitted — the row itself reflects the unchanged state on failure
        } finally {
            setAddOnBusyKey(null);
        }
    };
    const deactivateAddOn = async (key: string) => {
        setAddOnBusyKey(key);
        try {
            await apiRequest(`/charges/addons/${key}/deactivate`, { method: "POST" });
            refetch();
        } catch {
            // toast omitted — the row itself reflects the unchanged state on failure
        } finally {
            setAddOnBusyKey(null);
        }
    };
    const cancelAddOn = async (key: string) => {
        setAddOnBusyKey(key);
        try {
            await apiRequest(`/charges/addons/${key}/cancel`, { method: "POST" });
            refetch();
        } catch {
            // toast omitted — the row itself reflects the unchanged state on failure
        } finally {
            setAddOnBusyKey(null);
        }
    };
    const undoCancelAddOn = async (key: string) => {
        setAddOnBusyKey(key);
        try {
            await apiRequest(`/charges/addons/${key}/cancel/undo`, { method: "POST" });
            refetch();
        } catch {
            // toast omitted — the row itself reflects the unchanged state on failure
        } finally {
            setAddOnBusyKey(null);
        }
    };

    // Previous month for MoM delta (Fix 4)
    const prevMonthYear = month === 1 ? year - 1 : year;
    const prevMonthNum  = month === 1 ? 12 : month - 1;
    const { data: prevData } = useApi<ChargesData>(`/charges?year=${prevMonthYear}&month=${prevMonthNum}`);

    // Avoid navigation during render (can happen during logout / auth changes)
    useEffect(() => {
        if (!checkAdmin(userRole)) {
            navigate("/main", { replace: true });
        }
    }, [navigate, userRole]);

    if (!checkAdmin(userRole)) return null;

    const ROLE_LABELS: Record<number, string> = {
        1: t("charges.roleMedicalAssistant"),
        2: t("charges.roleDoctor"),
        3: t("charges.roleAdmin"),
        5: t("charges.roleAccounting"),
    };

    const PLAN_COMPARISON = [
        { label: t("charges.planCompBaseFee"),           basic: "€79",   plus: "€99",   pro: "€149" },
        { label: t("charges.planCompIncludedDoctors"),   basic: "1",     plus: "2",     pro: "2" },
        { label: t("charges.planCompIncludedStaff"),     basic: "—",     plus: "1",     pro: "2" },
        { label: t("charges.planCompExtraDoctor"),       basic: "+€39",  plus: "+€39",  pro: "+€39" },
        { label: t("charges.planCompExtraStaff"),        basic: "+€9,90", plus: "+€9,90", pro: "+€9,90" },
        { label: t("charges.planCompInvoicesPerDoctor"), basic: "100",   plus: "200",   pro: t("charges.planUnlimited") },
        { label: t("charges.planCompOverageRate"),       basic: "€0.79", plus: "€0.79", pro: "—" },
        { label: "SMS",                                  basic: "€0.09", plus: "€0.09", pro: t("charges.planIncluded") },
        { label: t("charges.letters"),                   basic: t("charges.planBilled"), plus: t("charges.planBilled"), pro: t("charges.planBilled") },
    ];


    // Keep previous data visible while new month loads to avoid layout jumps
    const staleData = useRef<ChargesData | null>(null);
    useEffect(() => { if (data) staleData.current = data; }, [data]);
    const displayData = data ?? staleData.current;

    // MoM delta — shared by total band, delta pill, and WhyCard
    const delta = (prevData && displayData) ? displayData.grandTotal - prevData.grandTotal : null;
    const deltaDir: "up" | "down" | null = delta !== null ? (delta >= 0 ? "up" : "down") : null;
    const deltaPct = (prevData && displayData && prevData.grandTotal > 0)
        ? Math.round((Math.abs(delta!) / prevData.grandTotal) * 100) : null;
    const prevLabel = new Date(prevMonthYear, prevMonthNum - 1, 1)
        .toLocaleDateString(undefined, { month: "long" });

    const whyItems =
        displayData && prevData ? buildWhyItems(displayData, prevData, t) : [];

    const ecoCount        = displayData?.invoices?.count ?? 0;
    const animatedEcoCount = useCountUp(ecoCount);
    const ecoTime  = fmtTimeSaved(t, animatedEcoCount);
    const ecoCo2   = fmtCo2(animatedEcoCount);

    const billingDueLabel = (() => {
        const due = new Date(year, month, 1);
        return due.toLocaleDateString(undefined, { day: "numeric", month: "short" }).toUpperCase();
    })();

    const isPeriodPastOrCurrent =
        year < now.getFullYear() ||
        (year === now.getFullYear() && month <= now.getMonth() + 1);

    const isViewingCurrentMonth =
        year === now.getFullYear() && month === now.getMonth() + 1;

    // Clamp month when year changes to registration year
    const registrationDate = user?.praxis?.createdAt ? new Date(user.praxis.createdAt) : null;
    const regYear  = registrationDate?.getFullYear() ?? now.getFullYear();
    const regMonth = registrationDate ? registrationDate.getMonth() + 1 : 1;

    const ALL_MONTHS = Array.from({ length: 12 }, (_, i) => ({
        value: i + 1,
        short: new Date(2000, i, 1).toLocaleString(undefined, { month: "short" }),
    }));

    const MONTHS = ALL_MONTHS.filter((m) => {
        if (year > regYear) return true;
        if (year === regYear) return m.value >= regMonth;
        return false;
    });

    const YEARS = Array.from(
        { length: now.getFullYear() - regYear + 1 },
        (_, i) => now.getFullYear() - i,
    );

    const popupContent = (key: DrawerKey, data: ChargesData | null): React.ReactNode => {
        if (!key || !data) return null;

        if (key === "plan") {
            const activePlan = data.planSummary.plan;
            return (
                <table className="dashboard-table">
                    <thead>
                        <tr>
                            <th></th>
                            {["BASIC", "PLUS", "PRO"].map((p, i) => (
                                <th key={p} style={{ color: PLAN_COLORS[i + 1], fontWeight: 700 }}>
                                    {p}{activePlan === i + 1 ? " ✓" : ""}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {PLAN_COMPARISON.map((row) => (
                            <tr key={row.label}>
                                <td style={{ fontWeight: 500 }}>{row.label}</td>
                                <td>{row.basic}</td>
                                <td>{row.plus}</td>
                                <td>{row.pro}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            );
        }

        if (key === "doctors") {
            const list = data.planSummary.roster ?? [];
            return (
                <table className="dashboard-table">
                    <thead>
                        <tr>
                            <th>{t("charges.colName")}</th>
                            <th>{t("charges.colRole")}</th>
                            <th>{t("charges.email", "Email")}</th>
                            <th>{t("charges.colSpecialty")}</th>
                            <th>{t("charges.colLastLogin")}</th>
                            <th>{t("charges.colBillingStatus", "Billing")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {list.length === 0 ? (
                            <tr><td colSpan={6} className="charges-empty">{t("charges.noDoctors")}</td></tr>
                        ) : list.map((d) => (
                            <tr key={d.id} className={d.removed ? "charges-row--removed" : undefined}>
                                <td>
                                    {d.name || <span className="charges-na">—</span>}
                                </td>
                                <td>{ROLE_LABELS[d.userRights] ?? <span className="charges-na">—</span>}</td>
                                <td>{d.email || <span className="charges-na">—</span>}</td>
                                <td>{(Array.isArray(d.specialty) ? d.specialty.join(", ") : d.specialty) || <span className="charges-na">—</span>}</td>
                                <td>{d.lastLogin ? fmtDate(d.lastLogin) : <span className="charges-na">{t("charges.never")}</span>}</td>
                                <td>
                                    {d.removed ? (
                                        <span className="charges-billing-badge charges-billing-badge--removed">
                                            {t("charges.billingStatusRemoved", { date: fmtDate(d.deletedAt as string), defaultValue: "Removed {{date}} · still billed" })}
                                        </span>
                                    ) : d.status === "extra" ? (
                                        <span className="charges-billing-badge charges-billing-badge--extra">
                                            {t("charges.billingStatusExtra", "Extra · billed")}
                                        </span>
                                    ) : (
                                        <span className="charges-billing-badge charges-billing-badge--included">
                                            {t("charges.billingStatusIncluded", "Included in plan")}
                                        </span>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            );
        }

        if (key === "invoices") {
            const STATUS_CONFIG: Record<number, { label: string; modifier: string }> = {
                0: { label: t("charges.statusOpen"),       modifier: "grey"   },
                2: { label: t("charges.statusPaid"),       modifier: "green"  },
                4: { label: t("charges.statusOverdue"),    modifier: "red"    },
                5: { label: t("charges.statusWrittenOff"), modifier: "grey"   },
            };
            return (
                <table className="dashboard-table">
                    <thead>
                        <tr>
                            <th>{t("charges.invoiceId")}</th>
                            <th>{t("charges.doctor")}</th>
                            <th>{t("charges.recipient")}</th>
                            <th>{t("charges.patient")}</th>
                            <th>{t("charges.colStatus")}</th>
                            <th>{t("charges.amount")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.invoiceRows.length === 0 ? (
                            <tr><td colSpan={6} className="charges-empty">{t("charges.noCharges")}</td></tr>
                        ) : data.invoiceRows.map((row) => {
                            const hasDunning = (row.dunning ?? 0) > 0;
                            const sc = STATUS_CONFIG[row.status];
                            return (
                                <tr key={row.invoiceId}>
                                    <td>
                                        <span
                                            className="charges-link"
                                            onClick={() => navigate(`/invoices/${row.invoiceId}`)}
                                        >
                                            {row.invoiceId}
                                        </span>
                                    </td>
                                    <td>{row.doctor || <span className="charges-na">—</span>}</td>
                                    <td>
                                        <span
                                            className="charges-link"
                                            onClick={() => navigate(`/recipients/${row.recipientId}`)}
                                        >
                                            {row.recipient}
                                        </span>
                                    </td>
                                    <td>
                                        <span
                                            className="charges-link"
                                            onClick={() => navigate(row.patientId ? `/patients/${row.patientId}` : "/patients")}
                                        >
                                            {row.patient}
                                        </span>
                                    </td>
                                    <td>
                                        {hasDunning ? (
                                            <Dunning level={row.dunning as number} />
                                        ) : sc ? (
                                            <span className={`dunning dunning--${sc.modifier}`}>{sc.label}</span>
                                        ) : (
                                            <span className="charges-na">—</span>
                                        )}
                                    </td>
                                    <td>€{fmt(row.amount)}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            );
        }

        if (key === "sms") {
            return (
                <table className="dashboard-table">
                    <thead>
                        <tr>
                            <th>{t("charges.recipient")}</th>
                            <th>{t("charges.phone")}</th>
                            <th>{t("charges.type")}</th>
                            <th>{t("charges.sentDate")}</th>
                            <th>{t("charges.cost")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.smsRows.length === 0 ? (
                            <tr><td colSpan={5} className="charges-empty">{t("charges.noCharges")}</td></tr>
                        ) : data.smsRows.map((row, i) => (
                            <tr key={i}>
                                <td>{row.recipient.name}</td>
                                <td>{row.recipient.tel ?? <span className="charges-na">—</span>}</td>
                                <td>
                                    <span className={`charges-sms-kind charges-sms-kind--${row.kind}`}>
                                        {t(`charges.kind_${row.kind}`)}
                                    </span>
                                </td>
                                <td>{fmtDate(row.sentDate)}</td>
                                <td>€{fmt(row.cost)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            );
        }

        if (key === "letters") {
            return (
                <table className="dashboard-table">
                    <thead>
                        <tr>
                            <th>{t("charges.recipient")}</th>
                            <th>{t("charges.address")}</th>
                            <th>{t("charges.letterDetails")}</th>
                            <th>{t("charges.sentDate")}</th>
                            <th>{t("charges.letterCostCol")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.letterRows.length === 0 ? (
                            <tr><td colSpan={5} className="charges-empty">{t("charges.noCharges")}</td></tr>
                        ) : data.letterRows.map((row, i) => (
                            <tr key={i}>
                                <td>{row.recipient.name}</td>
                                <td className="charges-address">
                                    {[row.recipient.street, row.recipient.number].filter(Boolean).join(" ")}
                                    {(row.recipient.plz || row.recipient.city) && (
                                        <>, {[row.recipient.plz, row.recipient.city].filter(Boolean).join(" ")}</>
                                    )}
                                </td>
                                <td className="charges-letter-details">
                                    {row.letterPages != null ? <span>{row.letterPages}p</span> : null}
                                    {row.letterColor  != null ? <span>{row.letterColor ? t("charges.color") : t("charges.bw")}</span> : null}
                                    {row.letterDuplex != null ? <span>{row.letterDuplex ? t("charges.duplex") : t("charges.simplex")}</span> : null}
                                    {row.letterPages == null && row.letterColor == null && <span className="charges-na">—</span>}
                                </td>
                                <td>{fmtDate(row.sentDate)}</td>
                                <td>
                                    €{fmt(row.cost)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            );
        }

        return null;
    };

    const POPUP_TITLES: Record<string, string> = {
        plan:     t("charges.popupPlans"),
        doctors:  t("charges.popupDoctors"),
        invoices: t("charges.invoices"),
        sms:      "SMS",
        letters:  t("charges.letters"),
    };

    const de = !i18n.language.startsWith("en");

    return (
        <div className="page settings-page">
            {loading && (
                <div className="charges-loading">
                    <div className="charges-loading__spinner" />
                </div>
            )}

            <div className="detail-breadcrumb">
                <button className="btn-back" onClick={() => navigate(-1)}>
                    <IoArrowBack />
                </button>
                <span className="detail-breadcrumb__parent" onClick={() => navigate(-1)}>
                    Dashboard
                </span>
                <IoChevronForward className="detail-breadcrumb__sep" />
                <span className="detail-breadcrumb__current">{t("charges.pageTitle", "Charges")}</span>
            </div>

            <PageHeader
                introOnly
                eyebrow={t("charges.billingEyebrow", "BILLING")}
                title={t("charges.pageTitle", "Charges")}
                lead={t("charges.pageLead", "Your monthly invoice and subscription overview")}
                accentColor="#6ee7b7"
            />

            <div className="charges-body">

            <div className="charges-period-card">
                <span className="charges-period-card__label">{t("charges.periodLabel", "PERIOD")}</span>
                <div className="charges-period-card__stepper">
                    <button
                        type="button"
                        className="charges-period-bar__arrow"
                        disabled={year === regYear && month <= regMonth}
                        onClick={() => { if (month === 1) { setMonth(12); setYear((p) => p - 1); } else setMonth((p) => p - 1); }}
                    >
                        ‹
                    </button>
                    <span className="charges-period-bar__label">
                        {new Date(year, month - 1, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" })}
                    </span>
                    <button
                        type="button"
                        className="charges-period-bar__arrow"
                        disabled={year === now.getFullYear() && month >= now.getMonth() + 1}
                        onClick={() => { if (month === 12) { setMonth(1); setYear((p) => p + 1); } else setMonth((p) => p + 1); }}
                    >
                        ›
                    </button>
                </div>
                {!isViewingCurrentMonth && (
                    <button
                        type="button"
                        className="charges-period-bar__today"
                        onClick={() => { setYear(now.getFullYear()); setMonth(now.getMonth() + 1); }}
                    >
                        {t("charges.today", "Today")}
                    </button>
                )}
                <span className="charges-period-card__spacer" />
                {displayData && isPeriodPastOrCurrent && (
                    <span className={`charges-period-bar__status${displayData.paid ? " charges-period-bar__status--paid" : ""}`}>
                        {displayData.paid
                            ? (displayData.paidAt ? `• ${t("charges.paidOn", { date: fmtDate(displayData.paidAt) })}` : `• ${t("charges.paid")}`)
                            : `• ${t("charges.dueOn", { date: billingDueLabel, defaultValue: "DUE {{date}}" })}`}
                    </span>
                )}
            </div>

            {/* ── C1: Total band ── */}
            {displayData && (() => {
                const paid = displayData.paid;
                const paidDate = displayData.paidAt ? fmtDate(displayData.paidAt) : null;
                const periodLabel = new Date(year, month - 1, 1)
                    .toLocaleDateString(undefined, { month: "long", year: "numeric" })
                    .toUpperCase();

                return (
                    <div className="charges-total-band">
                        <div className="charges-total-band__left">
                            <p className="charges-total-band__eyebrow">
                                <span className={`dot dot--${paid ? "paid" : "pending"}`} />
                                {paid && paidDate
                                    ? t("charges.totalPaidEyebrow", { period: periodLabel, date: paidDate })
                                    : t("charges.totalEyebrow", { period: periodLabel })}
                            </p>
                            <span className="charges-total-band__amount">
                                €{fmt(+(displayData.grandTotal * 1.19).toFixed(2))}
                            </span>
                            <span className="charges-total-band__net">
                                €{fmt(displayData.grandTotal)} {t("charges.netShort", "net")}
                                {" · "}
                                €{fmt(+(displayData.grandTotal * 0.19).toFixed(2))} {t("charges.vatShort", "VAT (19%)")}
                                {" · "}
                                {t("charges.billedOn", { date: billingDueLabel, defaultValue: "billed {{date}}" })}
                            </span>
                        </div>
                        <div className="charges-total-band__delta">
                            {delta !== null && Math.abs(delta) > 0.01 && (
                                <div className="delta-row">
                                    <span className={`delta-pill delta-pill--${deltaDir}`}>
                                        {deltaDir === "up" ? "↑" : "↓"} €{fmt(Math.abs(delta))}
                                    </span>
                                    <span className="delta-row__lab">
                                        {t("charges.vsLastMonth", { defaultValue: "vs" })} {prevLabel}
                                        {deltaPct !== null && ` · ${deltaDir === "up" ? "+" : "−"}${deltaPct}%`}
                                    </span>
                                </div>
                            )}
                        </div>
                        <div className="charges-total-band__cta">
                            {!isViewingCurrentMonth && (
                            <button
                                type="button"
                                className="charges-total-band__btn charges-total-band__btn--primary"
                                disabled={pdfDownloading}
                                onClick={handleDownloadReceipt}
                            >
                                <TbDownload size={15} strokeWidth={1.8} />
                                {pdfDownloading
                                    ? t("charges.downloading", "Downloading…")
                                    : t("charges.downloadInvoice", "Download invoice")}
                            </button>
                            )}
                            {pdfError && (
                                <p className="charges-total-band__receipt-note charges-total-band__receipt-note--error">
                                    {pdfError}
                                </p>
                            )}
                            {!isViewingCurrentMonth && (
                                <p className="charges-total-band__receipt-note">
                                    {paid
                                        ? t("charges.receiptEmailed", "Also sent to your practice email after billing.")
                                        : t("charges.receiptAfterBilling", "A copy is also emailed after SEPA billing.")}
                                </p>
                            )}
                        </div>
                    </div>
                );
            })()}

            {/* ── C3: Subscription card ── */}
            {billing && displayData && (
                <div className="charges-sub-card">
                    <div className="charges-sub-card__info">
                        <div className="charges-sub-card__row1">
                            <span className="charges-badge-plan">
                                {PLAN_LABELS[displayData.planSummary.plan] ?? "BASIC"}
                            </span>
                            <span className={`charges-badge-status charges-badge-status--${billing.subscriptionStatus}`}>
                                {billing.subscriptionStatus === "active" ? "• " : ""}
                                {t(`stripe.status_${billing.subscriptionStatus}`, { defaultValue: billing.subscriptionStatus })}
                            </span>
                            {billing.mandate ? (
                                <button
                                    type="button"
                                    className="charges-sub-card__sepa charges-sub-card__sepa--btn"
                                    onClick={handleOpenIban}
                                >
                                    <TbCheck size={13} className="charges-sub-card__sepa-icon" />
                                    {t("charges.sepaOnFile", { iban: maskIban(billing.mandate.iban), defaultValue: "SEPA on file · {{iban}}" })}
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    className="charges-sub-card__sepa charges-sub-card__sepa--missing charges-sub-card__sepa--btn"
                                    onClick={handleOpenIban}
                                >
                                    {t("charges.noPaymentMethod", "No payment method on file")}
                                </button>
                            )}
                        </div>
                        <div className="charges-sub-card__meta">
                            {t("charges.subMetaEach", {
                                base: fmt(displayData.planSummary.baseFee),
                                doctors: displayData.planSummary.doctorCount,
                                staff: displayData.planSummary.staffCount,
                                defaultValue: "€{{base}} base + usage · billed 1st of each month · {{doctors}} doctors, {{staff}} staff",
                            })}
                        </div>
                        {billing.subscriptionStatus === "past_due" && (
                            <span className="charges-sub-card__past-due">
                                <TbAlertCircle size={14} /> {t("stripe.pastDueWarning")}
                            </span>
                        )}
                        {user?.praxis?.pendingBusinessPlan && user.praxis.pendingBusinessPlanFrom && (
                            <button
                                type="button"
                                className="charges-plan-pending charges-plan-pending--btn"
                                onClick={() => setCancelPlanChangeOpen(true)}
                            >
                                {t("charges.pendingPlan", {
                                    plan: PLAN_LABELS[user.praxis.pendingBusinessPlan],
                                    date: new Date(user.praxis.pendingBusinessPlanFrom).toLocaleDateString(undefined, { day: "numeric", month: "long" }),
                                })}
                            </button>
                        )}
                    </div>
                    <div className="charges-sub-card__actions">
                        <button
                            type="button"
                            className="charges-sub-card__btn"
                            onClick={() => { setSelectedPlan(displayData.planSummary.plan); setChangePlanOpen(true); setPlanDone(false); }}
                        >
                            <TbRefresh size={14} />
                            {t("charges.changePlan")}
                        </button>
                        <button
                            type="button"
                            className="charges-sub-card__btn charges-sub-card__btn--danger"
                            onClick={() => { setCancelOpen(true); setCancelDone(false); }}
                        >
                            <TbX size={14} />
                            {t("charges.cancelSubscription")}
                        </button>
                    </div>
                </div>
            )}


            <div className="charges-eco-banner">
                    <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 14, padding: "16px 20px", background: "#dcfce7", color: "#15803d" }}>
                        <TbLeaf size={28} style={{ flexShrink: 0, opacity: 0.85 }} />
                        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            <strong style={{ fontSize: 15, fontWeight: 700 }}>{t("charges.ecoTimeSaved", { time: ecoTime.est, defaultValue: "~{{time}} saved" })}</strong>
                            <span style={{ fontSize: 12, opacity: 0.75 }}>{t("charges.ecoInvoices", { count: animatedEcoCount, defaultValue: "{{count}} digital invoices" })}</span>
                        </div>
                    </div>
                    <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 14, padding: "16px 20px", background: "#dbeafe", color: "#1d4ed8" }}>
                        <TbCloudOff size={28} style={{ flexShrink: 0, opacity: 0.85 }} />
                        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            <strong style={{ fontSize: 15, fontWeight: 700 }}>{t("charges.ecoCo2", { co2: ecoCo2.co2kg, defaultValue: "{{co2}} kg CO₂ avoided" })}</strong>
                            <span style={{ fontSize: 12, opacity: 0.75 }}>{t("charges.ecoSheets", { sheets: ecoCo2.sheets, defaultValue: "{{sheets}} sheets of paper" })}</span>
                        </div>
                    </div>
                    <button
                        type="button"
                        style={{ position: "absolute", bottom: 6, right: 10, padding: 0, border: "none", background: "none", fontSize: 11, color: "rgba(0,0,0,0.3)", cursor: "pointer", textDecoration: "underline" }}
                        onClick={() => setMethodologyOpen(true)}
                    >
                        {t("charges.methodology", "methodology")}
                    </button>
            </div>
            {methodologyOpen && (
                <Popup
                    title={t("charges.methodologyTitle", "Estimates methodology")}
                    message={
                        <p className="charges-methodology-text">
                            {t("charges.methodologyBody", {
                                defaultValue: "Time saved: ~5 minutes per digital invoice vs. manual post. Paper: ~2 sheets per invoice. CO₂: ~10 g per sheet avoided. Figures are illustrative, not audited.",
                            })}
                        </p>
                    }
                    actions={[{ label: t("charges.close"), onClick: () => setMethodologyOpen(false) }]}
                    onClose={() => setMethodologyOpen(false)}
                />
            )}


            {/* ── This month line items (always all rows) ── */}
            {displayData && (() => {
                const monthRows = buildMonthLineRows(displayData, t);
                const showDoctorStrip = monthRows.some((r) => r.key === "invoiceOverage" && r.expandable);

                return (
                    <section className="charges-lines-card" data-testid="charges-this-month">
                        <header className="charges-lines-card__head">
                            <h2 className="charges-lines-card__title">{t("charges.thisMonth", "This month")}</h2>
                            {showDoctorStrip && (
                                <span className="charges-lines-card__hint">
                                    {t("charges.overageHint", "click an overage row to see usage")}
                                </span>
                            )}
                        </header>
                        <div className="charges-lines-card__body">
                            {monthRows.map((row) => (
                                <ChargesLineRow
                                    key={`${row.label}-${row.key ?? "static"}`}
                                    row={row}
                                    doctorExpanded={doctorExpanded}
                                    onToggleDoctor={() => setDoctorExpanded((e) => !e)}
                                    onOpen={() => row.key && row.key !== "invoiceOverage" && setOpen(row.key)}
                                    byDoctor={displayData.invoices.byDoctor}
                                />
                            ))}
                            {(displayData.discount ?? 0) > 0 && (
                                <div className="charges-line-item charges-line-item--discount">
                                    <div className="charges-line-item__top">
                                        <span className="charges-line-item__label">
                                            {displayData.discountPercentage
                                                ? t("charges.discountWithPercentage", { pct: displayData.discountPercentage })
                                                : t("charges.discount")}
                                        </span>
                                        <span className="charges-line-item__value charges-line-item__value--discount">
                                            −€{fmt(displayData.discount)}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </section>
                );
            })()}

            {/* ── PVS savings calculator ── */}
            {displayData && (() => {
                const pvs = computePvsComparison(pvsSettings, displayData.invoices.count, displayData.invoices.amount, displayData.grandTotal);
                const negative = pvs !== null && pvs.savings < 0;
                const modifier = pvs ? " charges-pvs-card--positive" : "";
                return (
                    <section className={`charges-pvs-card${modifier}`}>
                        <TbCoinEuro size={26} strokeWidth={1.8} className="charges-pvs-card__icon" />
                        <div className="charges-pvs-card__text">
                            <span className="charges-pvs-card__label">{t("charges.pvsSavingsLabel")}</span>
                            {pvs ? (
                                <strong className="charges-pvs-card__value">
                                    €{fmt(Math.abs(pvs.savings))}
                                    {negative && <span className="charges-pvs-card__value-suffix"> {t("charges.pvsSavingsNegativeSuffix")}</span>}
                                </strong>
                            ) : (
                                <span className="charges-pvs-card__empty">{t("charges.pvsNotConfigured")}</span>
                            )}
                        </div>
                        <button type="button" className="charges-pvs-card__config-btn" onClick={handleOpenPvs}>
                            {t("charges.pvsConfigButton")}
                        </button>
                    </section>
                );
            })()}

            {/* ── Add-ons (admin-granted features outside the plan tier) ── */}
            {displayData && displayData.planSummary.addOns?.some((a) => a.billable) && (
                <section className="charges-lines-card" data-testid="charges-addons">
                    <header className="charges-lines-card__head">
                        <h2 className="charges-lines-card__title">{t("charges.addOns", "Add-ons")}</h2>
                    </header>
                    <div className="charges-lines-card__body">
                        {displayData.planSummary.addOns.filter((a) => a.billable).map((a) => (
                            <div key={a.key} className="charges-line-item">
                                <div className="charges-line-item__top">
                                    <span className="charges-line-item__label">{a.label}</span>
                                    <span className="charges-line-item__value">€{fmt(a.fee)}</span>
                                </div>
                                <div className="charges-line-item__sub charges-line-item__sub--row">
                                    {a.cancelEffectiveFrom ? (
                                        <>
                                            <span>
                                                {t("charges.addOnCancelsOn", { date: fmtDate(a.cancelEffectiveFrom), defaultValue: "Cancels {{date}}" })}
                                            </span>
                                            <button
                                                type="button"
                                                className="charges-line-item__action"
                                                disabled={addOnBusyKey === a.key}
                                                onClick={() => undoCancelAddOn(a.key)}
                                            >
                                                {t("charges.undo", "Undo")}
                                            </button>
                                        </>
                                    ) : (
                                        <button
                                            type="button"
                                            className="charges-line-item__action"
                                            disabled={addOnBusyKey === a.key}
                                            onClick={() => cancelAddOn(a.key)}
                                        >
                                            {t("charges.cancel", "Cancel")}
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            </div>{/* end charges-body */}

            {/* ── Update IBAN popup ── */}
            {ibanOpen && (
                <Popup
                    title={t("charges.updateIban")}
                    message={
                        <div className="charges-sepa-popup">
                            <p className="charges-sepa-popup__hint">{t("charges.updateIbanHint")}</p>
                            <label className="charges-sepa-popup__label">{t("settings.iban")}</label>
                            <input
                                className="charges-sepa-popup__input"
                                value={ibanValue}
                                onChange={(e) => setIbanValue(e.target.value.toUpperCase())}
                                onBlur={(e) => setIbanValue(normalizeIban(e.target.value))}
                                placeholder="DE00 0000 0000 0000 0000 00"
                                autoFocus
                            />
                            <label className="charges-sepa-popup__label">{t("settings.bic")}</label>
                            <input
                                className="charges-sepa-popup__input"
                                value={bicValue}
                                onChange={(e) => setBicValue(e.target.value.toUpperCase())}
                                placeholder="DEUTDEFFXXX"
                            />
                            {ibanError  && <div className="charges-sepa-popup__error"><TbAlertCircle /> {ibanError}</div>}
                            {ibanSaved  && <div className="charges-sepa-popup__success"><TbCheck /> {t("charges.ibanSaved")}</div>}
                        </div>
                    }
                    actions={[
                        {
                            label: ibanSaving ? "..." : t("settings.saveChanges"),
                            onClick: handleSaveIban,
                            disabled: ibanSaving || ibanSaved,
                        },
                        { label: t("charges.close"), onClick: () => setIbanOpen(false) },
                    ]}
                    onClose={() => setIbanOpen(false)}
                />
            )}

            {/* ── PVS savings calculator config popup ── */}
            {pvsOpen && (
                <Popup
                    title={t("charges.pvsConfigButton")}
                    message={
                        <div className="charges-sepa-popup">
                            <label className="charges-sepa-popup__label">{t("charges.pvsCommissionLabel")}</label>
                            <input
                                className="charges-sepa-popup__input"
                                type="number"
                                min={0}
                                max={100}
                                step="0.1"
                                value={pvsCommission}
                                onChange={(e) => setPvsCommission(e.target.value)}
                                autoFocus
                            />
                            <label className="charges-sepa-popup__label">{t("charges.pvsPerInvoiceLabel")}</label>
                            <input
                                className="charges-sepa-popup__input"
                                type="number"
                                min={0}
                                max={50}
                                step="0.01"
                                value={pvsPerInvoice}
                                onChange={(e) => setPvsPerInvoice(e.target.value)}
                            />
                            <label className="charges-sepa-popup__label">{t("charges.pvsPerMonthLabel")}</label>
                            <input
                                className="charges-sepa-popup__input"
                                type="number"
                                min={0}
                                max={5000}
                                step="0.01"
                                value={pvsPerMonth}
                                onChange={(e) => setPvsPerMonth(e.target.value)}
                            />
                            {pvsError && <div className="charges-sepa-popup__error"><TbAlertCircle /> {pvsError}</div>}
                            {pvsSaved && <div className="charges-sepa-popup__success"><TbCheck /> {t("charges.pvsSaved")}</div>}
                        </div>
                    }
                    actions={[
                        { label: t("charges.close"), onClick: () => setPvsOpen(false) },
                        {
                            label: t("charges.pvsResetButton"),
                            onClick: handleResetPvs,
                            disabled: pvsSaving || pvsSaved,
                        },
                        {
                            label: pvsSaving ? "..." : t("settings.saveChanges"),
                            onClick: handleSavePvs,
                            disabled: pvsSaving || pvsSaved,
                            variant: "primary",
                        },
                    ]}
                    onClose={() => setPvsOpen(false)}
                />
            )}

            {/* ── Cancel subscription popup ── */}
            {cancelOpen && (
                <Popup
                    title={t("charges.cancelSubscription")}
                    message={
                        cancelDone ? (
                            <p className="charges-request-done">{t("charges.cancelDone")}</p>
                        ) : (
                            <div className="charges-cancel-popup">
                                <p className="charges-cancel-popup__notice">{t("charges.cancelNotice")}</p>
                            </div>
                        )
                    }
                    actions={cancelDone
                        ? [{ label: t("popup.ok"), variant: "primary" as const, onClick: () => setCancelOpen(false) }]
                        : [
                            { label: t("popup.cancel"), variant: "default" as const, onClick: () => setCancelOpen(false) },
                            { label: cancelSending ? "..." : t("charges.cancelConfirm"), variant: "danger" as const, disabled: cancelSending, onClick: handleCancelSubscription },
                        ]
                    }
                    onClose={() => setCancelOpen(false)}
                />
            )}

            {displayData && (
                <>
                    {delta !== null && prevData && (
                        <WhyCard
                            momDelta={delta}
                            prevLabel={prevLabel}
                            items={whyItems}
                            t={t}
                            onComparePlans={() => {
                                setSelectedPlan(displayData.planSummary.plan);
                                setChangePlanOpen(true);
                                setPlanDone(false);
                            }}
                        />
                    )}


                    {/* ── Popup ── */}
                    {open && (
                        <Popup
                            title={POPUP_TITLES[open]}
                            message={
                                <div className="popup-table">
                                    {popupContent(open, displayData)}
                                </div>
                            }
                            actions={[{ label: t("charges.close"), onClick: () => setOpen(null) }]}
                            onClose={() => setOpen(null)}
                            className="popup--xl"
                        />
                    )}

                    {/* ── Cancel pending plan change popup ── */}
                    {cancelPlanChangeOpen && user?.praxis?.pendingBusinessPlan && (
                        <Popup
                            title={t("charges.cancelPlanChangeTitle", "Cancel plan change")}
                            message={t("charges.cancelPlanChangeMsg", {
                                plan: PLAN_LABELS[user.praxis.pendingBusinessPlan],
                                date: new Date(user.praxis.pendingBusinessPlanFrom!).toLocaleDateString(undefined, { day: "numeric", month: "long" }),
                                defaultValue: "The switch to {{plan}} scheduled for {{date}} will be cancelled. You stay on your current plan.",
                            })}
                            actions={[
                                { label: t("charges.close"), onClick: () => setCancelPlanChangeOpen(false) },
                                { label: cancelPlanChangeSending ? "..." : t("charges.cancelPlanChangeConfirm", "Cancel change"), variant: "danger" as const, disabled: cancelPlanChangeSending, onClick: handleCancelPlanChange },
                            ]}
                            onClose={() => setCancelPlanChangeOpen(false)}
                        />
                    )}

                    {/* ── Change Plan popup ── */}
                    {changePlanOpen && (
                        <Popup
                            title={t("charges.changePlan")}
                            message={
                                <div>
                                <p className="charges-plan-note">{t("charges.changePlanNote")}</p>
                                <div className="charges-plan-cards">
                                    {([1, 2, 3] as const).map((planId) => {
                                        const color  = PLAN_COLORS[planId];
                                        const active = selectedPlan === planId;
                                        const isCurrent = displayData.planSummary.plan === planId;
                                        return (
                                            <div
                                                key={planId}
                                                className={`charges-plan-card${active ? " charges-plan-card--selected" : ""}`}
                                                style={{ borderColor: active ? color : undefined }}
                                                onClick={() => setSelectedPlan(planId)}
                                            >
                                                <span className="charges-plan-card__name" style={{ color }}>
                                                    {PLAN_LABELS[planId]}
                                                    {isCurrent && <span className="charges-plan-card__current"> {t("charges.currentPlan")}</span>}
                                                </span>
                                                <span className="charges-plan-card__price">
                                                    {`€${[79, 99, 149][planId - 1]}/mo`}
                                                </span>
                                                <ul className="charges-plan-card__features">
                                                    <li>
                                                        {planId === 1 ? "1" : "2"}{" "}
                                                        {planId === 1 ? t("charges.planCompDoctorSingular") : t("charges.planCompDoctorPlural")}
                                                    </li>
                                                    {planId !== 1 && (
                                                        <li>{planId === 2 ? "1" : "2"} {t("charges.planCompStaffIncluded")}</li>
                                                    )}
                                                    <li>{planId <= 2 ? `${planId === 1 ? 100 : 200} ${t("charges.invoices")}` : t("charges.planUnlimited")} {planId <= 2 ? `/ ${t("charges.doctor")}` : ""}</li>
                                                    <li>SMS: {planId <= 2 ? "€0.09" : t("charges.planIncluded")}</li>
                                                </ul>
                                            </div>
                                        );
                                    })}
                                </div>
                                <p className="charges-addon-list__title">{t("charges.extraFeatures")}</p>
                                <div className="charges-addon-list">
                                    {/* Not real add-ons (no backend AddOnKey) — extra doctors/staff are metered,
                                        billed automatically per head over the plan's included count once actually
                                        added in Settings → Team, not a toggle. These rows are purely a
                                        discoverability link there, on every plan (including Basic — one extra
                                        doctor/admin or staff member is allowed for a fee, same as Plus/Pro). */}
                                    <div className="charges-addon-list__row">
                                        <span className="charges-addon-list__name">{t("charges.planCompExtraDoctor")}</span>
                                        <button
                                            type="button"
                                            className="charges-addon-list__toggle"
                                            onClick={() => {
                                                setChangePlanOpen(false);
                                                navigate("/settings?section=team");
                                            }}
                                        >
                                            {t("charges.addOnAdd", { price: "39", defaultValue: "Add · +€{{price}}/mo" })}
                                        </button>
                                    </div>
                                    <div className="charges-addon-list__row">
                                        <span className="charges-addon-list__name">{t("charges.planCompExtraStaff")}</span>
                                        <button
                                            type="button"
                                            className="charges-addon-list__toggle"
                                            onClick={() => {
                                                setChangePlanOpen(false);
                                                navigate("/settings?section=team");
                                            }}
                                        >
                                            {t("charges.addOnAdd", { price: "9,90", defaultValue: "Add · +€{{price}}/mo" })}
                                        </button>
                                    </div>
                                    {ADDON_INFO.map((addon) => {
                                        const previewPlan = selectedPlan ?? displayData.planSummary.plan;
                                        const included = previewPlan >= addon.minPlan;
                                        const active = !!displayData.planSummary.addOns?.some((a) => a.key === addon.key);
                                        const busy = addOnBusyKey === addon.key;
                                        return (
                                            <div
                                                key={addon.key}
                                                className={`charges-addon-list__row${included || active ? " charges-addon-list__row--on" : ""}`}
                                            >
                                                <span className="charges-addon-list__name">{t(addon.labelKey)}</span>
                                                {included ? (
                                                    <span className="charges-addon-list__status charges-addon-list__status--included">
                                                        {t("charges.planIncluded")}
                                                    </span>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        className={`charges-addon-list__toggle${active ? " charges-addon-list__toggle--active" : ""}`}
                                                        disabled={busy}
                                                        onClick={() => (active ? deactivateAddOn(addon.key) : activateAddOn(addon.key))}
                                                    >
                                                        {active
                                                            ? t("charges.addOnAdded", "Added")
                                                            : t("charges.addOnAdd", { price: addon.price.toFixed(2).replace(".", ","), defaultValue: "Add · +€{{price}}/mo" })}
                                                    </button>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                                </div>
                            }
                            actions={[
                                { label: t("charges.close"), onClick: () => setChangePlanOpen(false) },
                                {
                                    label: planSending ? "..." : t("charges.confirmPlan"),
                                    variant: "primary" as const,
                                    onClick: async () => {
                                        if (!selectedPlan || selectedPlan === displayData.planSummary.plan) {
                                            setChangePlanOpen(false);
                                            return;
                                        }
                                        setPlanSending(true);
                                        try {
                                            const result = await apiRequest<{
                                                businessPlan: number;
                                                pendingBusinessPlan: number;
                                                pendingBusinessPlanFrom: string;
                                            }>(
                                                "/praxis/plan",
                                                {
                                                    method: "PATCH",
                                                    body: JSON.stringify({ plan: selectedPlan }),
                                                },
                                                { "Content-Type": "application/json" },
                                            );
                                            updateUser({
                                                praxis: {
                                                    ...user!.praxis,
                                                    pendingBusinessPlan:     result.pendingBusinessPlan,
                                                    pendingBusinessPlanFrom: result.pendingBusinessPlanFrom,
                                                },
                                            });
                                            setChangePlanOpen(false);
                                            refetch();
                                        } finally {
                                            setPlanSending(false);
                                        }
                                    },
                                },
                            ]}
                            onClose={() => setChangePlanOpen(false)}
                            className="popup--xl"
                        />
                    )}
                </>
            )}
        </div>
    );
}
